import Token from '~/api/Token';
import RestX from '~/RestX';
import LogService from './LogService';
import MessageService from './MessageService';
import DialogService from './DialogService';
import IntercomService from './IntercomService';
import BackgroundProcessesService from './BackgroundProcessesService';

import GoogleFlow from '~/utils/socialAuth/GoogleFlow';
import FacebookFlow from '~/utils/socialAuth/FacebookFlow';
import LinkedInFlow from '~/utils/socialAuth/LinkedInFlow';
import WeChatFlow from '~/utils/socialAuth/WeChatFlow';

class AuthService {
    // Gets the userId. Returns a promise with null if not logged in or on failure.
    static getUserId() {
        // If not logged in, there is no user-id, return null
        if (!this.isLoggedIn()) {
            return Promise.resolve(null);
        }

        // Fetch the user data and return the id
        // This is most likely cached
        return RestX.getInterfaces()
            .user.me.get()
            .then(data => {
                data = data.data || data;
                if (data.id) {
                    return data.id;
                }
                return null;
            })
            .catch(error => {
                console.log(error);
                return null;
            });
    }

    static isAuthenticated() {
        return Token.getAccess() !== undefined;
    }

    static isLoggedIn() {
        return RestX.getInterfaces().logInModule.isLoggedIn();
    }

    static setIsLoggedIn(value) {
        RestX.getInterfaces().logInModule.setIsLoggedIn(value);
    }

    static async loadUserData() {
        await RestX.getInterfaces().user.me.get({ forceUpdate: true }); // Gets basic user data
        await RestX.getInterfaces().university.connection.get({
            forceUpdate: true,
        }); // Get university connections
    }

    static logOut() {
        Token.remove();
        RestX.getInterfaces().logInModule.setIsLoggedIn(false);
        RestX.getInterfaces().user.clear();
        RestX.getInterfaces().profile.clear();
        RestX.getInterfaces().university.clear();
        RestX.getInterfaces().candidate.clear();

        LogService.resetIdentity();
        IntercomService.reset();

        RestX.getInterfaces().logInModule.setOpen(true);
    }

    // Authenticates with an authorization code recieved from an external token
    static async authenticate(authCode, provider) {
        if (!authCode) {
            return Promise.reject();
        }

        // Validate provider
        let accessTokenPromise = null;
        switch (provider) {
            case 'Google':
                accessTokenPromise = GoogleFlow.getAccessToken(authCode);
                break;
            case 'Facebook':
                accessTokenPromise = FacebookFlow.getAccessToken(authCode);
                break;
            case 'LinkedIn':
                accessTokenPromise = LinkedInFlow.getAccessToken(authCode);
                break;
            case 'WeChat':
                accessTokenPromise = WeChatFlow.getAccessToken(authCode);
                break;
            default:
                return Promise.reject();
        }

        // Get access token and refresh token
        return accessTokenPromise
            .then(res => res.data)
            .then(async tokens => {
                if (tokens && tokens.accessToken) {
                    // Set tokens
                    const accessToken = tokens.accessToken;
                    const refreshToken = tokens.refreshToken;
                    Token.setAccess(accessToken);
                    Token.setRefresh(refreshToken);

                    // Initialize log in
                    RestX.getInterfaces()
                        .logInModule.setIsLoggedIn(true)
                        .then(() => {
                            IntercomService.setUserData(
                                IntercomService.SocialAuthLogin(provider)
                            );
                        });
                }
                return tokens;
            })
            .catch(err => {
                err = err.response ? err.response.data : err;
                const error =
                    err instanceof Array && err.length > 0 ? err[0] : err;

                // Check for 'invalid provider' code, which means the user tried to authenticate with wrong provider.
                if (error.code === 'InvalidProvider') {
                    DialogService.openErrorDialog(error.description);
                }
                return err;
            });
    }
}

export default AuthService;
